import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '@lmc-app/core/services/auth.service';
import { TokenStorageService } from '@lmc-app/core/services/token-storage.service';
import { Store } from '@ngrx/store';
import { logout, State } from '@lmc-app/store';

/**
 * HTTP Interceptor to handle 401 errors and automatically refresh tokens
 *
 * Flow:
 * 1. Intercept all HTTP responses
 * 2. If 401 error occurs, get refresh token from localStorage and call refresh API
 * 3. Save new tokens to localStorage
 * 4. Retry the original request with the new access token in Authorization header
 * 5. If refresh fails, dispatch logout action
 * 6. Queue multiple requests during token refresh to prevent multiple refresh calls
 */
@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
	private isRefreshing = false;
	private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

	constructor(
		private readonly authService: AuthService,
		private readonly tokenStorage: TokenStorageService,
		private readonly store: Store<State>
	) {}

	intercept(
		request: HttpRequest<unknown>,
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		return next.handle(request).pipe(
			catchError((error) => {
				// Only handle 401 Unauthorized errors
				if (error instanceof HttpErrorResponse && error.status === 401) {
					return this.handle401Error(request, next, error);
				}

				// Pass through other errors
				return throwError(() => error);
			})
		);
	}

	private handle401Error(
		request: HttpRequest<any>,
		next: HttpHandler,
		error: HttpErrorResponse
	): Observable<HttpEvent<any>> {
		// Don't try to refresh if the request is a login request
		if (request.url.includes('/auth/login')) {
			return throwError(() => error);
		}

		// Don't try to refresh if the request is already a refresh token request
		if (request.url.includes('/auth/refreshToken')) {
			this.handleRefreshFailure();
			return throwError(() => new Error('Token refresh failed'));
		}

		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshTokenSubject.next(null);

			const refreshToken = this.tokenStorage.getRefreshToken();
			if (!refreshToken) {
				this.isRefreshing = false;
				this.handleRefreshFailure();
				return throwError(() => new Error('No refresh token available'));
			}

			return this.authService.refreshToken(refreshToken).pipe(
				switchMap((response: RefreshTokenResponse) => {
					this.isRefreshing = false;

					// Save new tokens to localStorage
					this.tokenStorage.saveTokens(response.accessToken, response.refreshToken);

					// Notify all waiting requests
					this.refreshTokenSubject.next(response.accessToken);

					// Retry the original request with the new access token
					return next.handle(this.addToken(request, response.accessToken));
				}),
				catchError((err) => {
					this.isRefreshing = false;
					this.handleRefreshFailure();
					return throwError(() => err);
				})
			);
		} else {
			// Token refresh is already in progress — wait for it to complete
			return this.refreshTokenSubject.pipe(
				filter((token) => token !== null),
				take(1),
				switchMap((token) => {
					return next.handle(this.addToken(request, token!));
				})
			);
		}
	}

	private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
		return request.clone({
			setHeaders: {
				Authorization: `Bearer ${token}`
			}
		});
	}

	private handleRefreshFailure(): void {
		this.store.dispatch(logout());
	}
}